import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  styled,
} from "@mui/material";
import ArchitectureOutlinedIcon from "@mui/icons-material/ArchitectureOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { ReactComponent as LeftArrowIcon } from "../../assets/svg/LeftArrowIcon.svg";
import {
  actionDeliveryMemo,
  deleteDeliveryApprovalFile,
  deleteDeliveryMemo,
  getDeliveryApprovalById,
  getDeliveryMemoById,
  getProjectBillingMilestones,
  patchDeliveryMemo,
  submitDeliveryMemo,
  uploadDeliveryFile,
} from "../../services/contractorService";
import Deliverables from "../RequestForApproval/components/Deliverables";
import UploadGrid from "../RequestForApproval/components/UploadGrid";

const PrimaryButton = styled(Button)(() => ({
  backgroundColor: "#4D6BF6",
  color: "#fff",
  textTransform: "none",
  fontWeight: 500,
  borderRadius: "10px",
  boxShadow: "none",
  padding: "8px 18px",
  "&:hover": { backgroundColor: "#3B5BDB", boxShadow: "none" },
  "&.Mui-disabled": { backgroundColor: "#A8B5F8", color: "#fff" },
}));

const SecondaryButton = styled(Button)(() => ({
  backgroundColor: "#fff",
  color: "#0D0D0E",
  textTransform: "none",
  fontWeight: 500,
  borderRadius: "10px",
  border: "1px solid #D7D8D9",
  boxShadow: "none",
  padding: "8px 18px",
  "&:hover": { backgroundColor: "#F9FAFB", boxShadow: "none" },
  "&.Mui-disabled": { color: "#9CA3AF" },
}));

const SHOP_DRAWINGS_ICON = (
  <ArchitectureOutlinedIcon sx={{ fontSize: 20, color: "#4D6BF6" }} />
);
const BOQS_ICON = (
  <ReceiptLongOutlinedIcon sx={{ fontSize: 20, color: "#4D6BF6" }} />
);
const MATERIAL_SPECS_ICON = (
  <CategoryOutlinedIcon sx={{ fontSize: 20, color: "#4D6BF6" }} />
);
const DELIVERY_PROOFS_ICON = (
  <VerifiedOutlinedIcon sx={{ fontSize: 20, color: "#4D6BF6" }} />
);
const INVOICE_ICON = (
  <RequestQuoteOutlinedIcon sx={{ fontSize: 20, color: "#4D6BF6" }} />
);

// Tag (FormData key) for the three primary file sections, matching the
// approval flow contract verbatim.
const FILE_SECTIONS = [
  { key: "shopDrawings", tag: "Shop Drawing", title: "Shop Drawings" },
  { key: "boqs", tag: "Boqs", title: "BOQs" },
  {
    key: "materialSpecs",
    tag: "Material Specs",
    title: "Material Specifications",
  },
];

const SECTION_ICON = {
  shopDrawings: SHOP_DRAWINGS_ICON,
  boqs: BOQS_ICON,
  materialSpecs: MATERIAL_SPECS_ICON,
};

const STATUS_PILL_COLORS = {
  PENDING: { bg: "#FEF3C7", text: "#B45309" },
  APPROVED: { bg: "#DCFCE7", text: "#15803D" },
  REJECTED: { bg: "#FEE2E2", text: "#B91C1C" },
  DRAFT: { bg: "#F1F2F4", text: "#6B7280" },
  ARCHIVE: { bg: "#F1F2F4", text: "#6B7280" },
};

const formatStatusLabel = (status) =>
  String(status || "")
    .replace(/[_-]+/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const StatusPill = ({ status }) => {
  const key = String(status || "").toUpperCase();
  const palette = STATUS_PILL_COLORS[key] || {
    bg: "#F1F2F4",
    text: "#374151",
  };
  return (
    <Typography
      sx={{
        fontSize: 12,
        fontWeight: 600,
        fontFamily: "Inter Tight",
        backgroundColor: palette.bg,
        color: palette.text,
        borderRadius: "999px",
        px: 1.25,
        py: 0.25,
        textTransform: "capitalize",
        lineHeight: 1.6,
      }}
    >
      {formatStatusLabel(status)}
    </Typography>
  );
};

const SectionHeader = ({ icon, title, subtitle }) => (
  <Box sx={{ mb: 1.5 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {icon}
      <Typography
        sx={{
          fontSize: 16,
          fontWeight: 600,
          color: "#0D0D0E",
          fontFamily: "Inter Tight",
        }}
      >
        {title}
      </Typography>
    </Box>
    {subtitle && (
      <Typography
        sx={{
          fontSize: 12,
          color: "#9CA3AF",
          fontFamily: "Inter Tight",
          mt: 0.25,
          ml: 3.5,
        }}
      >
        {subtitle}
      </Typography>
    )}
  </Box>
);

const parseDeliverables = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

// Map a server design_files[] entry to our internal { id, url, ... } shape.
const fileItemFromServer = (f) => ({
  id: f.id,
  url: f.image_url || f.path,
  extension: f.extension || f.file_extention,
  name: (f.image_url || f.path || "").split("/").pop(),
});

// Pull the single invoice file from the memo response. Backend stores it
// on `data.delivery_memo.invoice` as a plain URL string with no separate
// file id. Removing the invoice in the UI is local-only until the user
// uploads a replacement and saves (PATCH then sends the new invoice_url).
const extractInvoice = (memoResp) => {
  if (!memoResp) return [];
  const raw =
    memoResp.delivery_memo?.invoice ||
    memoResp.invoice ||
    memoResp.data?.delivery_memo?.invoice ||
    memoResp.data?.invoice ||
    null;
  if (!raw) return [];
  if (typeof raw === "string") {
    return [
      {
        id: null,
        url: raw,
        extension: "application/pdf",
        name: raw.split("/").pop() || "invoice.pdf",
        fromServer: true,
      },
    ];
  }
  if (raw.id != null || raw.image_url || raw.path || raw.url) {
    return [{ ...fileItemFromServer(raw), fromServer: true }];
  }
  return [];
};

// Case-insensitive tag lookup so `"Boqs"`, `"BOQs"`, and
// `"Material Specifications"` all route to the right slot.
const resolveSectionKey = (tag) => {
  const t = String(tag || "").trim().toLowerCase();
  if (t === "shop drawing" || t === "shop drawings") return "shopDrawings";
  if (t === "boq" || t === "boqs") return "boqs";
  if (
    t === "material spec" ||
    t === "material specs" ||
    t === "material specification" ||
    t === "material specifications"
  ) {
    return "materialSpecs";
  }
  return null;
};

// Decide whether a file goes in the PDF tile row or the image row.
// Order of precedence:
//   1. `extension` mime-type hint (only when it's a real, specific type)
//   2. URL suffix (".pdf", ".png", ...)
//   3. Default to IMAGES — the shared /delivery-upload endpoint stores
//      every blob as "application/octet-stream" without sniffing, so a
//      generic extension is the signal that the backend couldn't tell.
//      Per spec, that case should land in the image row.
const classifyFileBucket = (extension, url) => {
  const e = String(extension || "").toLowerCase();
  if (e.includes("pdf")) return "pdfs";
  if (e.startsWith("image/")) return "images";
  const u = String(url || "").toLowerCase();
  if (/\.pdf(\?|$|#)/.test(u)) return "pdfs";
  if (/\.(png|jpe?g|gif|webp|bmp|svg)(\?|$|#)/.test(u)) return "images";
  return "images";
};

// Memo response: design_files[] are the section-tagged files (Shop
// Drawing / Boqs / Material Specs), images[] are the proof attachments
// (type: "delivery_memo_proof"). Approval response: design_files[] only.
const splitDesignFilesByTag = (designFiles) => {
  const out = {
    shopDrawings: { pdfs: [], images: [] },
    boqs: { pdfs: [], images: [] },
    materialSpecs: { pdfs: [], images: [] },
  };
  (Array.isArray(designFiles) ? designFiles : []).forEach((f) => {
    const key = resolveSectionKey(f.tag);
    if (!key) return;
    const item = fileItemFromServer(f);
    const bucket = classifyFileBucket(item.extension, item.url);
    out[key][bucket].push(item);
  });
  return out;
};

const isFileObject = (x) => x instanceof File;
const itemId = (x) => (x && !isFileObject(x) && x.id != null ? x.id : null);

export default function RequestForPayment() {
  const { id, milestoneId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // ---- routing context ----
  const memoIdParam = searchParams.get("memo_id");
  const forceViewMode = searchParams.get("view") === "1";
  const projectId = searchParams.get("project_id") || id;
  const proposalId = searchParams.get("proposal_id") || id;

  const [memoId, setMemoId] = useState(memoIdParam || null);
  const [memoStatus, setMemoStatus] = useState("");
  const [version, setVersion] = useState(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);

  const [milestone, setMilestone] = useState(null);
  // Cap for timeline date pickers / validators. Payment is logged
  // *after the fact*, so dates must be ≤ the milestone's due_date (or
  // ≤ today when the milestone has no due_date). No lower bound — past
  // dates are intentionally allowed.
  const paymentDateCap = useMemo(() => {
    const today = new Date().toISOString().split("T")[0];
    const due = milestone?.due_date || milestone?.dueDate;
    if (!due) return today;
    const dueIso = String(due).split("T")[0];
    // Re-clamp to today if the due date is somehow in the future
    // beyond a sane window — we never want to allow future selections.
    return dueIso < today ? dueIso : today < dueIso ? dueIso : today;
  }, [milestone?.due_date, milestone?.dueDate]);
  const [deliverables, setDeliverables] = useState([]);
  const [shopDrawings, setShopDrawings] = useState({ pdfs: [], images: [] });
  const [boqs, setBoqs] = useState({ pdfs: [], images: [] });
  const [materialSpecs, setMaterialSpecs] = useState({
    pdfs: [],
    images: [],
  });
  // Delivery proofs are a flat list (PDFs + images mixed together) since
  // they go on the wire as numbered keys (delivery_memo_1_proof, delivery_memo_2_proof, ...)
  // not under a section tag.
  const [proofs, setProofs] = useState({ pdfs: [], images: [] });
  // Invoice is a single PDF sent under the `invoice` FormData key. State is
  // an array of 0 or 1 entry (File for new uploads, {id, url, ...} after a
  // GET) so we can reuse UploadGrid.
  const [invoice, setInvoice] = useState([]);

  // delivery_approval_id forwarded on save when the milestone has an
  // APPROVED approval (so backend can carry over its design files).
  const [deliveryApprovalId, setDeliveryApprovalId] = useState(null);

  const sectionState = { shopDrawings, boqs, materialSpecs, proofs };
  const sectionSetters = {
    shopDrawings: setShopDrawings,
    boqs: setBoqs,
    materialSpecs: setMaterialSpecs,
    proofs: setProofs,
  };

  // Refs for scroll-into-view on validation failure. When a section is
  // missing required content the page smooth-scrolls to it and the toast
  // narrates what's wrong, so users land where they need to act.
  const timelineRef = useRef(null);
  const shopDrawingsRef = useRef(null);
  const boqsRef = useRef(null);
  const materialSpecsRef = useRef(null);
  const proofsRef = useRef(null);
  const invoiceRef = useRef(null);
  const sectionRefs = {
    shopDrawings: shopDrawingsRef,
    boqs: boqsRef,
    materialSpecs: materialSpecsRef,
    proofs: proofsRef,
  };
  const scrollToRef = (ref) => {
    if (ref?.current?.scrollIntoView) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const isReadOnly =
    forceViewMode || (!!memoStatus && memoStatus.toUpperCase() !== "DRAFT");

  const sectionStateRef = useRef(sectionState);
  sectionStateRef.current = sectionState;

  const loadAll = useCallback(
    async ({ silent = false, memoIdOverride } = {}) => {
      if (!projectId || !milestoneId) return;
      if (!silent) setLoading(true);
      // Allow callers (like Save after POST) to force a specific memo id
      // when the React state hasn't caught up yet — the useCallback
      // closure still holds the old memoId.
      const effectiveMemoId =
        memoIdOverride != null ? memoIdOverride : memoId;
      try {
        // 1. Pull milestone (and its delivery_memos[] / delivery_approvals[])
        // from the project-level billing-milestones endpoint.
        const msRes = await getProjectBillingMilestones(projectId);
        const list = Array.isArray(msRes?.data)
          ? msRes.data
          : Array.isArray(msRes)
            ? msRes
            : [];
        const found = list.find((m) => String(m.id) === String(milestoneId));
        if (!found) return;
        setMilestone(found);

        // Timeline only renders when the milestone has deliverables.
        const dels = parseDeliverables(found.deliverables).map((d, idx) => ({
          id: d.id || `deliverable-${idx}`,
          deliverable_description: d.deliverable_description || d.name || "",
          start_date: d.start_date || "",
          end_date: d.end_date || "",
          sub_activities: Array.isArray(d.sub_activities)
            ? d.sub_activities
            : [],
        }));
        setDeliverables(dels);

        // 2. Pick up the latest memo on the milestone if one exists. The
        // CTA on the previous screen forwards the id via ?memo_id, but we
        // fall back to the milestone's delivery_memos[] for resilience.
        const milestoneMemos = Array.isArray(found.delivery_memos)
          ? found.delivery_memos
          : [];
        const latestMemo = milestoneMemos[0] || null;
        const resolvedMemoId = effectiveMemoId || latestMemo?.id || null;

        if (resolvedMemoId) {
          if (!effectiveMemoId) setMemoId(String(resolvedMemoId));
          try {
            const mRes = await getDeliveryMemoById(resolvedMemoId);
            const m = mRes?.delivery_memo || mRes?.data || null;
            const designFiles =
              mRes?.design_files || mRes?.delivery_memo?.design_files || [];
            const proofImages =
              mRes?.images || mRes?.delivery_memo?.images || [];
            if (m) {
              setMemoStatus(m.status || "");
              setVersion(m.version ?? null);
            }
            const split = splitDesignFilesByTag(designFiles);
            setShopDrawings(split.shopDrawings);
            setBoqs(split.boqs);
            setMaterialSpecs(split.materialSpecs);

            // Proofs come from a separate `images` array on the response.
            // The backend persists them flat — one File per numbered key.
            const proofPdfs = [];
            const proofImgs = [];
            (Array.isArray(proofImages) ? proofImages : []).forEach((p) => {
              const item = fileItemFromServer(p);
              const ext = String(item.extension || "").toLowerCase();
              if (ext.includes("pdf")) proofPdfs.push(item);
              else proofImgs.push(item);
            });
            setProofs({
              pdfs: proofPdfs,
              images: proofImgs,
            });
            setInvoice(extractInvoice(mRes));
            return;
          } catch (e) {
            console.warn("Failed to hydrate delivery memo", e);
          }
        }

        // 3. No memo yet — but if there's a non-DRAFT delivery_approval
        // (PENDING / APPROVED / REJECTED / etc.), prefill the 3 file
        // sections from its design_files. The id goes on the save payload
        // as delivery_approval_id so the backend can link the two.
        const approvals = Array.isArray(found.delivery_approvals)
          ? found.delivery_approvals
          : [];
        const usableApproval = approvals.find(
          (a) => String(a.status || "").toUpperCase() !== "DRAFT",
        );
        if (usableApproval?.id != null) {
          setDeliveryApprovalId(usableApproval.id);
          try {
            const aRes = await getDeliveryApprovalById(usableApproval.id);
            const designFiles =
              aRes?.design_files || aRes?.delivery_approval?.design_files || [];
            const split = splitDesignFilesByTag(designFiles);
            setShopDrawings(split.shopDrawings);
            setBoqs(split.boqs);
            setMaterialSpecs(split.materialSpecs);
          } catch (e) {
            console.warn("Failed to prefill from delivery approval", e);
          }
        }
      } catch (e) {
        console.error("Failed to load milestone", e);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [projectId, milestoneId, memoId],
  );

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const backToProject = () => {
    const qs = new URLSearchParams({
      managed_by: "contractor",
      project_id: String(projectId),
    });
    navigate(
      `/project/details/${proposalId}/billing-milestones?${qs.toString()}`,
    );
  };

  // ---- payload builders ----
  // After the upload-on-pick refactor, items in state are either:
  //   - a File (only during the brief upload-in-flight window), or
  //   - an uploaded URL entry { url, name, extension }, or
  //   - a server entry from a previous GET with an `id`.
  // Save payload includes the first two; server entries (id present)
  // are already attached on the memo and skipped to avoid duplicates.
  const keepForPayload = (item) => {
    if (!item) return false;
    if (item.id != null) return false; // already on server
    if (isFileObject(item)) return true; // upload still in flight
    return !!(item.url || item.image_url || item.path);
  };

  const buildSectionFiles = () => {
    const out = {};
    FILE_SECTIONS.forEach(({ key, tag }) => {
      const st = sectionState[key];
      const arr = [
        ...(st.pdfs || []),
        ...(st.images || []),
      ].filter(keepForPayload);
      if (arr.length) out[tag] = arr;
    });
    return out;
  };

  const buildProofFiles = () =>
    [...(proofs.pdfs || []), ...(proofs.images || [])].filter(keepForPayload);

  // Invoice is single-slot. Sent when it's a fresh upload (File or URL
  // entry); skipped when it's an existing server-attached invoice
  // (already present on the memo).
  const buildInvoiceFile = () => {
    const f = invoice[0];
    return keepForPayload(f) ? f : null;
  };

  const buildActivities = () =>
    (deliverables || []).map((d) => ({
      deliverable_description: d.deliverable_description || d.name || "",
      start_date: d.start_date || null,
      end_date: d.end_date || null,
    }));

  const sectionHasAnything = (key) => {
    const st = sectionStateRef.current[key];
    const pdfCount = Array.isArray(st.pdfs) ? st.pdfs.length : 0;
    const imgCount = Array.isArray(st.images) ? st.images.length : 0;
    return pdfCount > 0 || imgCount > 0;
  };

  // Save & Submit are disabled until something has been added: at least
  // one file (pending or already on server) OR an activity date filled.
  const hasAnyContent = useMemo(() => {
    const fileCount = [
      "shopDrawings",
      "boqs",
      "materialSpecs",
      "proofs",
    ].reduce((acc, key) => {
      const st = sectionState[key];
      const pdfs = Array.isArray(st.pdfs) ? st.pdfs.length : 0;
      const imgs = Array.isArray(st.images) ? st.images.length : 0;
      return acc + pdfs + imgs;
    }, 0);
    const anyActivity = (deliverables || []).some(
      (d) => d.start_date || d.end_date,
    );
    return fileCount > 0 || anyActivity || invoice.length > 0;
  }, [shopDrawings, boqs, materialSpecs, proofs, deliverables, invoice]); // eslint-disable-line react-hooks/exhaustive-deps

  const canSaveOrSubmit = !isReadOnly && hasAnyContent;

  // ---- file change handlers ----
  // Each newly-picked File hits POST /api/project/delivery-upload right
  // away. The entry is kept as a File while the request is in flight so
  // UploadGrid can preview it, then swapped for the returned URL entry
  // once the upload resolves. Removals of server-attached files call
  // DELETE /design-files/:id with body `{ tag: "proof" | "design_file" }`.
  const swapItemInSlot = (setter, slot, predicate, nextItem) => {
    setter((s) => {
      const arr = (s[slot] || []).map((x) => (predicate(x) ? nextItem : x));
      return { ...s, [slot]: arr };
    });
  };
  const dropItemFromSlot = (setter, slot, predicate) => {
    setter((s) => ({
      ...s,
      [slot]: (s[slot] || []).filter((x) => !predicate(x)),
    }));
  };

  const uploadNewFilesInSlot = async (setter, slot, nextArr, prevArr) => {
    const prevSet = new Set(prevArr);
    const newFiles = nextArr.filter(
      (x) => x instanceof File && !prevSet.has(x),
    );
    for (const file of newFiles) {
      try {
        const uploaded = await uploadDeliveryFile(file);
        if (uploaded?.url) {
          swapItemInSlot(
            setter,
            slot,
            (x) => x === file,
            {
              url: uploaded.url,
              mime_type:
                uploaded.mime_type ||
                file.type ||
                file.name?.split(".").pop() ||
                null,
              name: file.name,
              extension:
                uploaded.mime_type ||
                file.type ||
                file.name?.split(".").pop(),
            },
          );
        } else {
          toast.error(`Failed to upload ${file.name || "file"}`);
          dropItemFromSlot(setter, slot, (x) => x === file);
        }
      } catch (e) {
        console.error("Upload failed", e);
        toast.error(`Failed to upload ${file.name || "file"}`);
        dropItemFromSlot(setter, slot, (x) => x === file);
      }
    }
  };

  // Deletes any server-attached files in `removed`. DELETE body includes
  // `tag`: "proof" for delivery memo proofs, "design_file" for tagged
  // design-file sections (shop / BOQ / specs).
  const handleServerDeletes = async (removed, deleteTag = "design_file") => {
    const serverEntries = removed.filter((item) => itemId(item) != null);
    if (serverEntries.length === 0) return;
    await Promise.all(
      serverEntries.map(async (item) => {
        const id = itemId(item);
        try {
          const res = await deleteDeliveryApprovalFile(id, deleteTag);
          if (res?.success === false) {
            toast.error(res?.message || "Failed to delete file");
          }
        } catch (e) {
          console.warn("Failed to delete server file", id, e);
          toast.error(e?.message || "Failed to delete file");
        }
      }),
    );
    if (memoId) {
      await loadAll({ memoIdOverride: memoId });
    }
  };

  // Same trick as the approval page: keep server-attached "removed"
  // entries in state until the server call confirms + GET refetch
  // drops them. Locally-uploaded entries (no id) apply right away.
  const computeNextKeepingServerDeletes = (prev, nextArr) => {
    const nextSet = new Set(nextArr);
    const removed = prev.filter((p) => !nextSet.has(p));
    const pendingServerRemovals = new Set(
      removed.filter((item) => itemId(item) != null),
    );
    const kept = prev.filter(
      (p) => nextSet.has(p) || pendingServerRemovals.has(p),
    );
    const additions = nextArr.filter((item) => !prev.includes(item));
    return { effectiveNext: [...kept, ...additions], removed };
  };

  const handlePdfsChange = (key) => (nextArr) => {
    const prev = sectionStateRef.current[key].pdfs || [];
    const { effectiveNext, removed } = computeNextKeepingServerDeletes(
      prev,
      nextArr,
    );
    sectionSetters[key]((s) => ({ ...s, pdfs: effectiveNext }));
    uploadNewFilesInSlot(sectionSetters[key], "pdfs", nextArr, prev);
    const deleteTag = key === "proofs" ? "proof" : "design_file";
    handleServerDeletes(removed, deleteTag);
  };

  const handleImagesChange = (key) => (nextArr) => {
    const prev = sectionStateRef.current[key].images || [];
    const { effectiveNext, removed } = computeNextKeepingServerDeletes(
      prev,
      nextArr,
    );
    sectionSetters[key]((s) => ({ ...s, images: effectiveNext }));
    uploadNewFilesInSlot(sectionSetters[key], "images", nextArr, prev);
    const deleteTag = key === "proofs" ? "proof" : "design_file";
    handleServerDeletes(removed, deleteTag);
  };

  // Invoice is single-slot. The UploadGrid hands us a 0- or 1-entry
  // array; we still upload on pick and swap the entry once the URL
  // arrives. Removals are local-only (no API); the user must upload an
  // invoice again before Save/Submit will run a PATCH with invoice_url.
  const handleInvoiceChange = (nextArr) => {
    const prev = invoice;
    const trimmed = nextArr.slice(-1);
    const newFile = trimmed.find(
      (x) => x instanceof File && !prev.includes(x),
    );
    setInvoice(trimmed);
    if (newFile) {
      (async () => {
        try {
          const uploaded = await uploadDeliveryFile(newFile);
          if (uploaded?.url) {
            setInvoice((curr) =>
              curr.map((x) =>
                x === newFile
                  ? {
                      url: uploaded.url,
                      mime_type:
                        uploaded.mime_type ||
                        newFile.type ||
                        newFile.name?.split(".").pop() ||
                        null,
                      name: newFile.name,
                      extension:
                        uploaded.mime_type ||
                        newFile.type ||
                        newFile.name?.split(".").pop(),
                    }
                  : x,
              ),
            );
          } else {
            toast.error(`Failed to upload ${newFile.name || "invoice"}`);
            setInvoice((curr) => curr.filter((x) => x !== newFile));
          }
        } catch (e) {
          console.error("Invoice upload failed", e);
          toast.error(`Failed to upload ${newFile.name || "invoice"}`);
          setInvoice((curr) => curr.filter((x) => x !== newFile));
        }
      })();
    }
  };

  // ---- validation ----
  // Payment activities happened in the past (or up to the milestone's
  // due_date) — so we reject FUTURE dates here, not past ones. The cap
  // is `paymentDateCap`: milestone.due_date when set, else today.
  const validateTimeline = () => {
    if (!Array.isArray(deliverables) || deliverables.length === 0) return true;
    const cap = paymentDateCap;
    for (let i = 0; i < deliverables.length; i += 1) {
      const d = deliverables[i];
      const label =
        d.deliverable_description || d.name || `Deliverable ${i + 1}`;
      // On Save we accept blank (optional). On Submit we tighten elsewhere.
      if (!d.start_date && !d.end_date) continue;
      if (d.start_date && d.start_date > cap) {
        toast.error(`Start date cannot be after ${cap} for "${label}"`);
        return false;
      }
      if (d.end_date && d.end_date > cap) {
        toast.error(`End date cannot be after ${cap} for "${label}"`);
        return false;
      }
      if (d.start_date && d.end_date && d.end_date < d.start_date) {
        toast.error(`End date must be after start date for "${label}"`);
        return false;
      }
    }
    return true;
  };

  // Invoice is mandatory for both Save and Submit: an uploaded PDF URL
  // must be present (not an in-flight File, not an empty slot after a
  // local remove).
  const validateInvoice = () => {
    if (invoice.length === 0) {
      toast.error("Please upload the Invoice (PDF)");
      scrollToRef(invoiceRef);
      return false;
    }
    const f = invoice[0];
    if (isFileObject(f)) {
      toast.error("Please wait for the invoice upload to finish");
      scrollToRef(invoiceRef);
      return false;
    }
    const url = f.url || f.image_url || f.path;
    if (!url) {
      toast.error("Please upload the Invoice (PDF)");
      scrollToRef(invoiceRef);
      return false;
    }
    return true;
  };

  const validateForSubmit = () => {
    // Timeline (only if the milestone has deliverables) — every row needs
    // both dates filled. On any failure, scroll to the timeline card.
    // Future dates are rejected (capped by milestone.due_date or today);
    // past dates are explicitly allowed.
    if (Array.isArray(deliverables) && deliverables.length > 0) {
      const cap = paymentDateCap;
      for (let i = 0; i < deliverables.length; i += 1) {
        const d = deliverables[i];
        const label =
          d.deliverable_description || d.name || `Deliverable ${i + 1}`;
        if (!d.start_date) {
          toast.error(`Please select a start date for "${label}"`);
          scrollToRef(timelineRef);
          return false;
        }
        if (d.start_date > cap) {
          toast.error(`Start date cannot be after ${cap} for "${label}"`);
          scrollToRef(timelineRef);
          return false;
        }
        if (!d.end_date) {
          toast.error(`Please select an end date for "${label}"`);
          scrollToRef(timelineRef);
          return false;
        }
        if (d.end_date > cap) {
          toast.error(`End date cannot be after ${cap} for "${label}"`);
          scrollToRef(timelineRef);
          return false;
        }
        if (d.end_date < d.start_date) {
          toast.error(`End date must be after start date for "${label}"`);
          scrollToRef(timelineRef);
          return false;
        }
      }
    }
    // Mandatory on submit: BOQs, Delivery Proofs, and Invoice. Shop
    // Drawings and Material Specs are optional at submit time. Checked
    // top-down so the scroll lands on the first missing section.
    if (!sectionHasAnything("boqs")) {
      toast.error("Please upload BOQs");
      scrollToRef(boqsRef);
      return false;
    }
    if (!sectionHasAnything("proofs")) {
      toast.error("Please upload Delivery Proofs");
      scrollToRef(proofsRef);
      return false;
    }
    if (!validateInvoice()) return false;
    return true;
  };

  // ---- persist (POST when no memo id, PATCH otherwise) ----
  // Returns the resolved memo id so callers (Save) can refetch the
  // GET-by-id before React has flushed the new state.
  const persist = async () => {
    const filesPayload = buildSectionFiles();
    const proofFiles = buildProofFiles();
    const activities = buildActivities();
    const invoiceFile = buildInvoiceFile();
    if (memoId) {
      await patchDeliveryMemo(
        memoId,
        filesPayload,
        activities,
        proofFiles,
        deliveryApprovalId,
        invoiceFile,
      );
      return { memoId };
    }
    const res = await submitDeliveryMemo(
      milestoneId,
      filesPayload,
      activities,
      proofFiles,
      deliveryApprovalId,
      invoiceFile,
    );
    const newId =
      res?.delivery_memo?.id ||
      res?.data?.delivery_memo?.id ||
      res?.data?.id ||
      res?.id;
    if (newId != null) {
      setMemoId(String(newId));
      const status = res?.delivery_memo?.status || res?.data?.status;
      if (status) setMemoStatus(status);
    }
    return { memoId: newId != null ? String(newId) : null };
  };

  const handleSave = async () => {
    // Save is blocked until an invoice with a finished upload URL is
    // present (removing the invoice is local-only until they replace it).
    if (!validateInvoice()) return;
    setSaving(true);
    try {
      const { memoId: persistedId } = await persist();
      toast.success("Payment request saved");
      // Re-pull the memo (GET /delivery-memo/:memoId) so the UI reflects
      // the canonical server state — newly-assigned file ids, version
      // bumps, etc. Pass the resolved id explicitly: the useCallback
      // closure may still hold the pre-POST null.
      await loadAll({ memoIdOverride: persistedId });
    } catch (e) {
      console.error("Failed to save payment request", e);
      toast.error(e?.message || "Failed to save payment request");
    } finally {
      setSaving(false);
    }
  };

  // True when any section (incl. proofs / invoice) still has a `File`
  // instance — an upload is still mid-flight via /delivery-upload. We
  // pause Submit briefly so the action call doesn't race the upload.
  const hasPendingFiles = () => {
    const allKeys = ["shopDrawings", "boqs", "materialSpecs", "proofs"];
    const sectionPending = allKeys.some((key) => {
      const st = sectionState[key];
      if ((st.pdfs || []).some((p) => isFileObject(p))) return true;
      return (st.images || []).some((img) => isFileObject(img));
    });
    const invoicePending = invoice.some((p) => isFileObject(p));
    return sectionPending || invoicePending;
  };

  // True when any section (or proofs / invoice) holds an item that's
  // been uploaded but not yet attached to the memo row (URL entry with
  // no server `id`). Triggers an automatic PATCH right before Submit.
  const hasUnsavedUploads = () => {
    const allKeys = ["shopDrawings", "boqs", "materialSpecs", "proofs"];
    const lacksId = (arr) =>
      (arr || []).some((x) => x && !isFileObject(x) && x.id == null);
    const sectionDirty = allKeys.some((key) => {
      const st = sectionState[key];
      return lacksId(st.pdfs) || lacksId(st.images);
    });
    const invoiceDirty = invoice.some(
      (x) => x && !isFileObject(x) && x.id == null,
    );
    return sectionDirty || invoiceDirty;
  };

  // Click handler — only validates. The actual API calls happen in
  // `confirmSubmit` once the user confirms in the modal.
  const handleSubmit = () => {
    if (!memoId) return; // button is hidden in this state; defensive
    if (hasPendingFiles()) {
      toast.error("Please wait for file uploads to finish before submitting");
      return;
    }
    if (!validateForSubmit()) return;
    setSubmitConfirmOpen(true);
  };

  const closeSubmitConfirm = () => {
    if (submitting) return;
    setSubmitConfirmOpen(false);
  };

  // Confirm flow — PATCH first if there are unattached uploads, then
  // PUT /action with status=SUBMITTED. Pure PUT when nothing's dirty.
  const confirmSubmit = async () => {
    if (!memoId) return;
    setSubmitting(true);
    try {
      if (hasUnsavedUploads()) {
        await persist();
      }
      await actionDeliveryMemo(memoId, "SUBMITTED");
      toast.success("Payment request submitted");
      setSubmitConfirmOpen(false);
      backToProject();
    } catch (e) {
      console.error("Failed to submit payment request", e);
      toast.error(e?.message || "Failed to submit payment request");
    } finally {
      setSubmitting(false);
    }
  };

  // Dialog open/close — confirm flow lives in `confirmDeleteMemo`.
  const openDeleteModal = () => {
    if (!memoId) return;
    setDeleteModalOpen(true);
  };
  const closeDeleteModal = () => {
    if (deleting) return;
    setDeleteModalOpen(false);
  };

  const confirmDeleteMemo = async () => {
    if (!memoId) return;
    setDeleting(true);
    try {
      await deleteDeliveryMemo(memoId);
      toast.success("Payment request deleted");
      setDeleteModalOpen(false);
      backToProject();
    } catch (e) {
      console.error("Failed to delete memo", e);
      toast.error(e?.message || "Failed to delete payment request");
    } finally {
      setDeleting(false);
    }
  };

  const milestoneLabel = useMemo(
    () =>
      milestone?.description ||
      milestone?.name ||
      milestone?.group_name ||
      "Request for Payment",
    [milestone],
  );

  const showHeaderDelete =
    !!memoId &&
    String(memoStatus || "").toUpperCase() === "DRAFT" &&
    !forceViewMode;

  const showTimeline = Array.isArray(deliverables) && deliverables.length > 0;

  if (loading) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 8,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          padding: { xs: "2vh 4vw 1vh", md: "3vh 2.5vw 1.5vh" },
          gap: "10px",
          background:
            "linear-gradient(180deg, rgba(77, 107, 246, 0.30) 0%, rgba(255, 255, 255, 0.00) 100%)",
          flexShrink: 0,
        }}
      >
        <LeftArrowIcon onClick={backToProject} style={{ cursor: "pointer" }} />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            flexWrap: "wrap",
            flex: 1,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              color: "#0D0D0E",
              fontSize: { xs: "15px", md: "16px" },
              fontWeight: 500,
            }}
          >
            {isReadOnly ? "View Payment" : "Request for Payment"} —{" "}
            {milestoneLabel}
          </Box>
          {version != null && (
            <Box
              sx={{
                fontSize: 12,
                fontWeight: 600,
                color: "#0D0D0E",
                fontFamily: "Inter Tight",
                backgroundColor: "#F1F2F4",
                borderRadius: "999px",
                px: 1.25,
                py: 0.25,
              }}
            >
              Version-{version}
            </Box>
          )}
          {memoStatus && <StatusPill status={memoStatus} />}
        </Box>
        {showHeaderDelete && (
          <Button
            onClick={openDeleteModal}
            disabled={deleting}
            startIcon={<DeleteOutlineIcon fontSize="small" />}
            sx={{
              color: "#DC2626",
              border: "1px solid #FECACA",
              borderRadius: "10px",
              backgroundColor: "#fff",
              textTransform: "none",
              fontFamily: "Inter Tight",
              fontWeight: 500,
              fontSize: 14,
              padding: "6px 14px",
              boxShadow: "none",
              whiteSpace: "nowrap",
              "&:hover": {
                backgroundColor: "#FEF2F2",
                borderColor: "#FCA5A5",
                boxShadow: "none",
              },
            }}
          >
            Delete Delivery Memo
          </Button>
        )}
      </Box>

      <Box
        component="fieldset"
        disabled={isReadOnly}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          padding: { xs: "2vh 4vw 2vh", md: "2vh 2vw 2vh 2vw" },
          display: "flex",
          flexDirection: "column",
          gap: 3,
          border: "none",
          margin: 0,
          minWidth: 0,
        }}
      >
        {showTimeline && (
          <Box
            ref={timelineRef}
            sx={{
              border: "1px solid #E5E7EB",
              borderRadius: "12px",
              backgroundColor: "#fff",
              p: 2.5,
              scrollMarginTop: 16,
            }}
          >
            <SectionHeader
              icon={
                <CalendarTodayOutlinedIcon
                  sx={{ fontSize: 18, color: "#4D6BF6" }}
                />
              }
              title="Timeline"
            />
            <Deliverables
              deliverables={deliverables}
              onChange={setDeliverables}
              mode="past"
              maxDate={paymentDateCap}
            />
          </Box>
        )}

        {FILE_SECTIONS.map(({ key, title }) => {
          const st = sectionState[key];
          return (
            <Box
              key={key}
              ref={sectionRefs[key]}
              sx={{
                border: "1px solid #E5E7EB",
                borderRadius: "12px",
                p: 2.5,
                scrollMarginTop: 16,
              }}
            >
              <UploadGrid
                title={title}
                subtitle="Upload images & PDFs"
                icon={SECTION_ICON[key]}
                pdfFiles={st.pdfs}
                onPdfFilesChange={handlePdfsChange(key)}
                images={st.images}
                onImagesChange={handleImagesChange(key)}
                readOnly={isReadOnly}
              />
            </Box>
          );
        })}

        <Box
          ref={proofsRef}
          sx={{
            border: "1px solid #E5E7EB",
            borderRadius: "12px",
            p: 2.5,
            scrollMarginTop: 16,
          }}
        >
          <UploadGrid
            title="Delivery Proofs"
            subtitle="Upload images & PDFs"
            icon={DELIVERY_PROOFS_ICON}
            pdfFiles={proofs.pdfs}
            onPdfFilesChange={handlePdfsChange("proofs")}
            images={proofs.images}
            onImagesChange={handleImagesChange("proofs")}
            readOnly={isReadOnly}
          />
        </Box>

        <Box
          ref={invoiceRef}
          sx={{
            border: "1px solid #E5E7EB",
            borderRadius: "12px",
            p: 2.5,
            scrollMarginTop: 16,
          }}
        >
          <UploadGrid
            title="Invoice"
            subtitle="Upload a single PDF (required)"
            icon={INVOICE_ICON}
            pdfFiles={invoice}
            onPdfFilesChange={handleInvoiceChange}
            allowImages={false}
            maxPdfs={1}
            readOnly={isReadOnly}
          />
        </Box>
      </Box>

      <Box
        sx={{
          padding: { xs: "12px 4vw", md: "1.5vh 2vw" },
          backgroundColor: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          flexShrink: 0,
          borderTop: { xs: "1px solid #E5E7EB", md: "none" },
        }}
      >
        <SecondaryButton onClick={backToProject}>
          {isReadOnly ? "Back" : "Cancel"}
        </SecondaryButton>
        {!isReadOnly && (
          <Box sx={{ display: "flex", gap: 1 }}>
            <SecondaryButton
              onClick={handleSave}
              disabled={!canSaveOrSubmit || saving || submitting}
            >
              {saving ? "Saving..." : "Save"}
            </SecondaryButton>
            {/* Submit only appears once the memo row exists (POST done at
                least once). Until then Save is the only primary action. */}
            {memoId && (
              <PrimaryButton
                onClick={handleSubmit}
                disabled={!canSaveOrSubmit || saving || submitting}
              >
                {submitting ? "Submitting..." : "Submit"}
              </PrimaryButton>
            )}
          </Box>
        )}
      </Box>

      <Dialog
        open={deleteModalOpen}
        onClose={closeDeleteModal}
        PaperProps={{
          sx: { borderRadius: "12px", width: "min(420px, 92vw)" },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "Inter Tight",
            fontSize: 18,
            fontWeight: 600,
            color: "#0D0D0E",
            pb: 0.5,
          }}
        >
          Delete Payment?
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography
            sx={{
              fontSize: 14,
              color: "#4B5563",
              fontFamily: "Inter Tight",
            }}
          >
            Are you sure you want to delete this payment details?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button
            onClick={closeDeleteModal}
            disabled={deleting}
            sx={{
              textTransform: "none",
              fontFamily: "Inter Tight",
              color: "#0D0D0E",
              border: "1px solid #D7D8D9",
              borderRadius: "10px",
              px: 2,
              "&:hover": { backgroundColor: "#F9FAFB" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteMemo}
            disabled={deleting}
            sx={{
              textTransform: "none",
              fontFamily: "Inter Tight",
              color: "#fff",
              backgroundColor: "#DC2626",
              borderRadius: "10px",
              px: 2,
              "&:hover": { backgroundColor: "#B91C1C" },
            }}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Submit confirmation — same shape as the delete modal but the
          primary action is blue and the body asks for confirmation
          before firing PATCH (if dirty) + PUT /action. */}
      <Dialog
        open={submitConfirmOpen}
        onClose={closeSubmitConfirm}
        PaperProps={{
          sx: { borderRadius: "12px", width: "min(420px, 92vw)" },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "Inter Tight",
            fontSize: 18,
            fontWeight: 600,
            color: "#0D0D0E",
            pb: 0.5,
          }}
        >
          Submit Payment Request?
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography
            sx={{
              fontSize: 14,
              color: "#4B5563",
              fontFamily: "Inter Tight",
            }}
          >
            Are you sure you want to submit the payment request?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button
            onClick={closeSubmitConfirm}
            disabled={submitting}
            sx={{
              textTransform: "none",
              fontFamily: "Inter Tight",
              color: "#0D0D0E",
              border: "1px solid #D7D8D9",
              borderRadius: "10px",
              px: 2,
              "&:hover": { backgroundColor: "#F9FAFB" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmSubmit}
            disabled={submitting}
            sx={{
              textTransform: "none",
              fontFamily: "Inter Tight",
              color: "#fff",
              backgroundColor: "#4D6BF6",
              borderRadius: "10px",
              px: 2,
              "&:hover": { backgroundColor: "#3B5BDB" },
            }}
          >
            {submitting ? "Submitting..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
